import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'
import {
    Controller,
    type UseControllerProps,
    useFormContext,
} from 'react-hook-form'
import { type OpcaoSelect, SelectField } from '../SelectField'

export type { OpcaoSelect }

type FormFieldSelectProps = {
    name: string
    label?: ReactNode
    options: OpcaoSelect[] | undefined
    rules?: UseControllerProps['rules']
    shouldUnregister?: boolean
    defaultValue?: unknown
    required?: boolean
    disabled?: boolean
    placeholder?: string
    helperText?: ReactNode
    className?: string
    /** Recebe o `value` da opção escolhida, já no tipo original. */
    onChange?: (value: any) => void
    /** Conteúdo à direita do campo, antes da seta (substitui o `endAdornment` do MUI). */
    endAdornment?: ReactNode
    /** Não reserva a linha auxiliar abaixo do campo — para layouts compactos. O erro continua sinalizado no próprio campo. */
    unHelperText?: boolean
}

/**
 * `SelectField` ligado ao react-hook-form. Toda a aparência mora lá; aqui fica
 * só o `Controller` e a herança de `disabled` do formulário.
 */
export const FormFieldSelect = ({
    name,
    label,
    options,
    rules,
    shouldUnregister,
    defaultValue = '',
    required,
    disabled,
    placeholder,
    helperText,
    unHelperText = false,
    className,
    onChange,
    endAdornment,
}: FormFieldSelectProps) => {
    const { control } = useFormContext()
    const desabilitado = disabled || (control as any)._options?.disabled

    return (
        <Controller
            name={name}
            rules={rules}
            control={control}
            defaultValue={defaultValue}
            shouldUnregister={shouldUnregister}
            render={({
                field: { onChange: onChangeCampo, onBlur, value },
                fieldState: { error },
            }) => (
                <SelectField
                    id={`${name}-select`}
                    data-testid={name}
                    label={label}
                    options={options}
                    value={value}
                    onValueChange={valor => {
                        onChangeCampo(valor)
                        onChange?.(valor)
                    }}
                    onBlur={onBlur}
                    error={error?.message ?? !!error}
                    required={required}
                    disabled={desabilitado}
                    placeholder={placeholder}
                    helperText={helperText}
                    unHelperText={unHelperText}
                    endAdornment={endAdornment}
                    className={cn('min-w-0', className)}
                />
            )}
        />
    )
}
